import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import { useTheme } from './hooks/useTheme';
import Navbar from './components/Navbar';
import Hero from './sections/Hero';
import About from './sections/About';
import Experience from './sections/Experience';
import Projects from './sections/Projects';
import Skills from './sections/Skills';
import Contact from './sections/Contact';
import Footer from './components/Footer';
import BackToTop from './components/BackToTop';
import NotFound from './NotFound';

const WritingList = React.lazy(() => import('./sections/WritingList'));
const WritingPost = React.lazy(() => import('./sections/WritingPost'));

interface HomePageProps {
  theme: string;
  toggleTheme: () => void;
}

function HomePage({ theme, toggleTheme }: HomePageProps) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.reveal');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Navbar theme={theme} toggleTheme={toggleTheme} />
      <Hero />
      <About />
      <Experience />
      <Projects />
      <Skills />
      <Contact />
      <Footer />
      <BackToTop />
    </>
  );
}

// Cloudflare redirects /resume before the app loads (see public/_redirects).
// This covers the dev server and anything that still routes here client-side.
function ResumeRedirect() {
  useEffect(() => {
    window.location.replace('/resume.pdf');
  }, []);

  return null;
}

function App() {
  const { theme, toggleTheme } = useTheme();

  return (
    <BrowserRouter>
      <div className="noise-overlay"></div>
      <Routes>
        <Route
          path="/"
          element={<HomePage theme={theme} toggleTheme={toggleTheme} />}
        />
        <Route path="/resume" element={<ResumeRedirect />} />
        <Route
          path="/writing"
          element={
            <Suspense fallback={<div className="loading-spinner"></div>}>
              <WritingList />
            </Suspense>
          }
        />
        <Route
          path="/writing/:slug"
          element={
            <Suspense fallback={<div className="loading-spinner"></div>}>
              <WritingPost />
            </Suspense>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;

